/**
 * Preiskonfiguration für SkyVision COMFORT
 * Basiert auf FIXED-Preisen plus Motoraufpreis
 *
 * COMFORT = FIXED + Motor(en)
 * - Mindestmaß: 600x600mm
 * - Max. Fläche: 2-fach bis 4m², 3-fach bis 3m²
 *
 * Motorlogik (Anzahl, nicht Preis):
 * - 2-fach: >2m² ODER Seite >2000mm → 2 Motoren, sonst 1 Motor
 * - 3-fach: >1,5m² ODER Seite >2000mm → 2 Motoren, sonst 1 Motor
 *
 * Preise kommen aus dem Zubehörkatalog (motor_single / motor_dual,
 * wcc103 / wcc106). Die Werte unten sind nur Fallback, falls der
 * Katalog nicht geladen ist.
 */

import { 
  FIXED_PRICE_TABLE_2FACH, 
  FIXED_PRICE_SURCHARGE_3FACH,
  SKYVISION_FIXED_CONFIG,
  FIXED_GLASS_OPTIONS
} from './priceConfigFixed';

// Comfort-spezifische Konfiguration
export const SKYVISION_COMFORT_CONFIG = {
  baseDimensions: {
    width: 600,    // Mindestbreite in mm
    length: 600,   // Mindestlänge in mm
    height: 210    // Standard-Höhe in mm für 2-fach Verglasung
  },
  glassTypes: {
    double: {
      name: '2-fach Isolierverglasung',
      description: 'Sehr gute Wärmedämmung (Ug-Wert 1,1)',
      minHeight: 210,
      maxArea: 4 // m²
    },
    triple: {
      name: '3-fach Isolierverglasung', 
      description: 'Maximale Energieeffizienz (Ug-Wert 0,7)',
      minHeight: 240,
      maxArea: 3 // m²
    }
  },
  motor: {
    /** Nur Fallback, wenn der Katalog nicht da ist. */
    pricePerMotor: 627.85,
    controls: {
      wcc103: {
        name: 'WCC103 Steuerung',
        description: 'Steuerungsinterface für 1 Motor',
        price: 290,
        motorCount: 1
      },
      wcc106: {
        name: 'WCC106 Steuerung',
        description: 'Steuerungsinterface für 2 Motoren',
        price: 515,
        motorCount: 2
      }
    }
  }
};

/** Katalogpreise für Comfort-Motor und Steuerung. */
export type ComfortCatalogPrices = {
  motorSingle?: number;
  motorDual?: number;
  wcc103?: number;
  wcc106?: number;
};

type CatalogPriceSource = {
  accessory_code?: string;
  id?: string | number;
  base_price?: number;
};

export function comfortPricesFromCatalog(
  catalog?: CatalogPriceSource[],
): ComfortCatalogPrices | undefined {
  if (!catalog?.length) return undefined;
  const pick = (code: string): number | undefined => {
    const item = catalog.find(
      (entry) => entry.accessory_code === code || String(entry.id ?? '') === code,
    );
    if (item?.base_price == null) return undefined;
    const value = Number(item.base_price);
    return Number.isFinite(value) ? value : undefined;
  };
  const prices: ComfortCatalogPrices = {
    motorSingle: pick('motor_single'),
    motorDual: pick('motor_dual'),
    wcc103: pick('wcc103'),
    wcc106: pick('wcc106'),
  };
  return prices.motorSingle != null ||
    prices.motorDual != null ||
    prices.wcc103 != null ||
    prices.wcc106 != null
    ? prices
    : undefined;
}

function motorPriceFromCatalog(
  motorCount: number,
  catalog?: ComfortCatalogPrices,
): number {
  if (motorCount === 2) {
    if (catalog?.motorDual != null) return catalog.motorDual;
    if (catalog?.motorSingle != null) return catalog.motorSingle * 2;
  } else if (catalog?.motorSingle != null) {
    return catalog.motorSingle;
  }
  return 0;
}

function controlPriceFromCatalog(
  controlType: 'wcc103' | 'wcc106' | null | undefined,
  motorCount: number,
  catalog?: ComfortCatalogPrices,
): number {
  if (!controlType) return 0;
  const expected = controlType === 'wcc103' ? 1 : 2;
  if (motorCount !== expected) return 0;
  if (controlType === 'wcc103' && catalog?.wcc103 != null) return catalog.wcc103;
  if (controlType === 'wcc106' && catalog?.wcc106 != null) return catalog.wcc106;
  return 0;
}

// Dimensionsgrenzen für SkyVision COMFORT
export const COMFORT_DIMENSIONS = {
  MIN_WIDTH: 600,   // 600mm Bestellmaß
  MAX_WIDTH: 1600,  // Basierend auf FIXED-Preistabelle
  MIN_LENGTH: 600,  // 600mm Bestellmaß  
  MAX_LENGTH: 3000, // Basierend auf FIXED-Preistabelle
  MIN_HEIGHT_DOUBLE: 210,
  MIN_HEIGHT_TRIPLE: 240,
  MAX_HEIGHT: 750,
  MAX_AREA_DOUBLE: 4, // m²
  MAX_AREA_TRIPLE: 3, // m²
  STEP_SIZE: 100   // 100mm Schritte
};

export type MotorRules = {
  dualAreaDouble: number;
  dualAreaTriple: number;
  dualMaxSide: number;
};

export const DEFAULT_MOTOR_RULES: MotorRules = {
  dualAreaDouble: 2,
  dualAreaTriple: 1.5,
  dualMaxSide: 2000,
};

export function parseMotorRules(raw: unknown): MotorRules {
  const source = (raw || {}) as Partial<MotorRules>;
  return {
    dualAreaDouble:
      Number(source.dualAreaDouble) > 0
        ? Number(source.dualAreaDouble)
        : DEFAULT_MOTOR_RULES.dualAreaDouble,
    dualAreaTriple:
      Number(source.dualAreaTriple) > 0
        ? Number(source.dualAreaTriple)
        : DEFAULT_MOTOR_RULES.dualAreaTriple,
    dualMaxSide:
      Number(source.dualMaxSide) > 0
        ? Number(source.dualMaxSide)
        : DEFAULT_MOTOR_RULES.dualMaxSide,
  };
}

/**
 * Berechnet die Anzahl der benötigten Motoren
 */
export const calculateRequiredMotors = (
  width: number,
  length: number,
  glassType: 'double' | 'triple',
  rules: MotorRules = DEFAULT_MOTOR_RULES
): number => {
  const area = (width * length) / 1000000; // m²
  const maxSide = Math.max(width, length);
  const areaLimit =
    glassType === 'triple' ? rules.dualAreaTriple : rules.dualAreaDouble;
  if (area > areaLimit || maxSide > rules.dualMaxSide) {
    return 2;
  }
  return 1;
};

/**
 * Rundet Dimensionen auf 100mm-Schritte auf
 */
const roundToStep = (value: number, step: number = 100): number => {
  return Math.ceil(value / step) * step;
};

/**
 * Sucht den Preis in der Tabelle mit Fallback-Logik
 */
const lookupPrice = (table: any, width: number, length: number): number => {
  const roundedWidth = roundToStep(width);
  const roundedLength = roundToStep(length);
  
  if (table[roundedWidth] && table[roundedWidth][roundedLength] !== undefined) {
    const price = table[roundedWidth][roundedLength];
    return price > 0 ? price : 0;
  }
  
  // Fallback: Suche nächstkleinere verfügbare Größe
  const availableWidths = Object.keys(table).map(Number).sort((a, b) => a - b);
  const availableLengths = Object.keys(table[availableWidths[0]] || {}).map(Number).sort((a, b) => a - b);
  
  let fallbackWidth = availableWidths.find(w => w >= roundedWidth) || availableWidths[availableWidths.length - 1];
  let fallbackLength = availableLengths.find(l => l >= roundedLength) || availableLengths[availableLengths.length - 1];
  
  if (table[fallbackWidth] && table[fallbackWidth][fallbackLength] !== undefined) {
    const price = table[fallbackWidth][fallbackLength];
    return price > 0 ? price : 0;
  }
  
  return 0;
};

/**
 * Berechnet den Preis für SkyVision COMFORT
 */
export const calculateComfortPrice = (
  width: number,
  length: number, 
  height: number,
  glassType: 'double' | 'triple' | null = 'double',
  controlType?: 'wcc103' | 'wcc106' | null,
  catalogPrices?: ComfortCatalogPrices,
  motorRules: MotorRules = DEFAULT_MOTOR_RULES,
): {
  basePrice: number;
  motorCount: number;
  motorPrice: number;
  controlPrice: number;
  heightSurcharge: number;
  glassSurcharge: number;
  totalPrice: number;
} => {
  // Basis-Preis aus FIXED-Tabelle (2-fach)
  let basePrice = lookupPrice(FIXED_PRICE_TABLE_2FACH, width, length);
  
  if (basePrice === 0) {
    console.warn(`Kein Preis gefunden für ${width}x${length}mm`);
    return {
      basePrice: 0,
      motorCount: 0,
      motorPrice: 0,
      controlPrice: 0,
      heightSurcharge: 0,
      glassSurcharge: 0,
      totalPrice: 0
    };
  }

  // Aufpreis für 3-fach Verglasung
  let glassSurcharge = 0;
  if (glassType === 'triple') {
    glassSurcharge = lookupPrice(FIXED_PRICE_SURCHARGE_3FACH, width, length);
    basePrice += glassSurcharge;
  }

  // Höhenaufschlag (2€ pro 10mm über 210mm)
  let heightSurcharge = 0;
  if (height > SKYVISION_COMFORT_CONFIG.baseDimensions.height) {
    const heightDiff = height - SKYVISION_COMFORT_CONFIG.baseDimensions.height;
    heightSurcharge = Math.round((heightDiff / 10) * 2);
    basePrice += heightSurcharge;
  }

  const motorCount = calculateRequiredMotors(
    width,
    length,
    glassType || 'double',
    motorRules,
  );
  const motorPrice = motorPriceFromCatalog(motorCount, catalogPrices);
  const controlPrice = controlPriceFromCatalog(controlType, motorCount, catalogPrices);
  const roundMoney = (value: number) => Math.round(value * 100) / 100;

  const totalPrice = roundMoney(basePrice + motorPrice + controlPrice);

  return {
    basePrice: Math.round(basePrice - glassSurcharge - heightSurcharge),
    motorCount,
    motorPrice: roundMoney(motorPrice),
    controlPrice: roundMoney(controlPrice),
    heightSurcharge,
    glassSurcharge,
    totalPrice
  };
};

/**
 * Validiert die angegebenen Dimensionen für COMFORT
 */
export const validateComfortDimensions = (
  width: number, 
  length: number, 
  height: number, 
  glassType: 'double' | 'triple' | null
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  const minHeight = glassType === 'triple' 
    ? COMFORT_DIMENSIONS.MIN_HEIGHT_TRIPLE 
    : COMFORT_DIMENSIONS.MIN_HEIGHT_DOUBLE;
  
  if (width < COMFORT_DIMENSIONS.MIN_WIDTH) {
    errors.push(`Mindestbreite: ${COMFORT_DIMENSIONS.MIN_WIDTH}mm`);
  }
  
  if (width > COMFORT_DIMENSIONS.MAX_WIDTH) {
    errors.push(`Maximale Breite: ${COMFORT_DIMENSIONS.MAX_WIDTH}mm`);
  }
  
  if (length < COMFORT_DIMENSIONS.MIN_LENGTH) {
    errors.push(`Mindestlänge: ${COMFORT_DIMENSIONS.MIN_LENGTH}mm`);
  }
  
  if (length > COMFORT_DIMENSIONS.MAX_LENGTH) {
    errors.push(`Maximale Länge: ${COMFORT_DIMENSIONS.MAX_LENGTH}mm`);
  }
  
  if (height < minHeight) {
    errors.push(`Mindesthöhe für ${glassType === 'triple' ? '3-fach' : '2-fach'} Verglasung: ${minHeight}mm`);
  }
  
  if (height > COMFORT_DIMENSIONS.MAX_HEIGHT) {
    errors.push(`Maximale Höhe: ${COMFORT_DIMENSIONS.MAX_HEIGHT}mm`);
  }
  
  const area = (width * length) / 1000000;
  const maxArea = glassType === 'triple' 
    ? COMFORT_DIMENSIONS.MAX_AREA_TRIPLE 
    : COMFORT_DIMENSIONS.MAX_AREA_DOUBLE;
    
  if (area > maxArea) {
    errors.push(`Maximale Fläche für ${glassType === 'triple' ? '3-fach' : '2-fach'} Verglasung: ${maxArea}m²`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Gibt eine detaillierte Preisaufschlüsselung zurück
 */
export const getComfortPriceBreakdown = (
  width: number,
  length: number,
  height: number,
  glassType: 'double' | 'triple' | null = 'double',
  controlType?: 'wcc103' | 'wcc106' | null
): {
  breakdown: string[];
  details: ReturnType<typeof calculateComfortPrice>;
} => {
  const details = calculateComfortPrice(width, length, height, glassType, controlType);
  const roundedWidth = roundToStep(width);
  const roundedLength = roundToStep(length);
  
  const breakdown = [
    `SkyVision COMFORT · ${roundedWidth} × ${roundedLength} mm · 2-fach: ${details.basePrice}€`,
    ...(details.glassSurcharge > 0 ? [`3-fach Verglasung Aufpreis: +${details.glassSurcharge}€`] : []),
    ...(details.heightSurcharge > 0 ? [`Höhenaufschlag (+${height - SKYVISION_COMFORT_CONFIG.baseDimensions.height}mm): +${details.heightSurcharge}€`] : []),
    `Motor (${details.motorCount}x): +${details.motorPrice}€`,
    ...(details.controlPrice > 0 ? [`Steuerung (${controlType?.toUpperCase()}): +${details.controlPrice}€`] : [])
  ];

  return {
    breakdown,
    details
  };
};

/**
 * Gibt die Mindestdimensionen zurück
 */
export const getComfortMinDimensions = () => {
  return {
    width: COMFORT_DIMENSIONS.MIN_WIDTH,
    length: COMFORT_DIMENSIONS.MIN_LENGTH,
    height: COMFORT_DIMENSIONS.MIN_HEIGHT_DOUBLE
  };
};

/**
 * Gibt die Glastypen mit ihren Eigenschaften zurück
 */
export const getComfortGlassTypes = () => {
  return {
    double: {
      name: SKYVISION_COMFORT_CONFIG.glassTypes.double.name,
      minHeight: SKYVISION_COMFORT_CONFIG.glassTypes.double.minHeight,
      maxArea: SKYVISION_COMFORT_CONFIG.glassTypes.double.maxArea
    },
    triple: {
      name: SKYVISION_COMFORT_CONFIG.glassTypes.triple.name,
      minHeight: SKYVISION_COMFORT_CONFIG.glassTypes.triple.minHeight,
      maxArea: SKYVISION_COMFORT_CONFIG.glassTypes.triple.maxArea
    }
  };
};

/**
 * Gibt die Dimensionsgrenzen zurück
 */
export const getComfortDimensions = () => {
  return COMFORT_DIMENSIONS;
};

/**
 * Gibt die verfügbaren Steuerungsoptionen zurück
 */
export const getComfortControls = (motorCount: number) => {
  if (motorCount === 1) {
    return [SKYVISION_COMFORT_CONFIG.motor.controls.wcc103];
  } else if (motorCount === 2) {
    return [SKYVISION_COMFORT_CONFIG.motor.controls.wcc106];
  }
  return [];
};

/**
 * COMFORT hat die gleichen Glasoptionen/Accessories wie FIXED
 */
export const COMFORT_GLASS_OPTIONS = FIXED_GLASS_OPTIONS;

/**
 * Berechnet den Preis für ein Glasoption/Accessory (gleich wie bei FIXED)
 */
export const calculateComfortAccessoryPrice = (
  accessoryId: keyof typeof COMFORT_GLASS_OPTIONS,
  width: number,
  length: number
): number => {
  const option = COMFORT_GLASS_OPTIONS[accessoryId];
  if (!option) return 0;
  
  const area = option.calculateArea(width, length);
  const calculatedPrice = option.pricePerSqm * area;
  
  return area < 1.0 ? option.minPrice : Math.round(calculatedPrice);
};
